import * as React from "react";
import { AppStore, PostscriptStore } from "Stores/index";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { inject, observer } from "mobx-react";
import {
    POSTSCRIPT_CHECK_OPTIONS,
    PostscriptCategory,
    PostscriptCategoryCheck,
} from "Stores/superStore";
import { SelectWrapper } from "Components/Common/SelectWrapper";
import {CrudModal} from "Components/Common/CrudModal";
import {ColumnDef, flexRender, getCoreRowModel, getPaginationRowModel, useReactTable} from "@tanstack/react-table";
import {ActionWrapper} from "Components/Common/ActionWrapper";
import PageTitle from "Components/Common/PageTitle";
import {useInternalContext} from "@/contexts/internal";

interface CategoriesListProps{
    postscriptStore?: PostscriptStore
    appStore?: AppStore
}

interface CategoriesListState{
    showModal: boolean
    selectedCategory?: PostscriptCategory
    formNameValue: string
    formLevelValue: string
    formCheckValue: {label: string, value: string}
}

export const CategoriesList = inject("postscriptStore", "appStore")(observer((props: CategoriesListProps) => {
    const checkOptions = POSTSCRIPT_CHECK_OPTIONS.map((str: string) => ({label: str, value: str}));

    const columns: ColumnDef<PostscriptCategory>[] = [{
        header: "Name",
        accessorKey: "name",
        minSize: 300,
    }, {
        header: "Level",
        accessorKey: "level",
        minSize: 80,
    }, {
        header: "Check",
        accessorKey: "check",
        minSize: 200,
    }, {
        header: "Actions",
        cell: (props) => renderActionButton(props.row.original),
    }];

    const [state, setState] = React.useState<CategoriesListState>({
        showModal: false,
        selectedCategory: undefined,
        formNameValue: "",
        formLevelValue: "1",
        formCheckValue: checkOptions[1],
    });
    const internalContext = useInternalContext();

    React.useEffect(() => {
        if (!props.postscriptStore) return;
        props.postscriptStore.fetchCategories();
        internalContext.setRenderActionButton(renderCreateButton());
        return () => {
            internalContext.setRenderActionButton(null);
        };
    }, [props.postscriptStore]);

    const handleNameChange = (ev: any) => {
        setState(prevState => ({...prevState, formNameValue: ev.target.value}));
    };

    const handleLevelChange = (ev: any) => {
        setState(prevState => ({...prevState, formLevelValue: ev.target.value}));
    };

    const handleCheckChange = (next: any) => {
        setState(prevState => ({...prevState, formCheckValue: next}));
    };

    const isValid = () => state.formNameValue && state.formCheckValue && !isNaN(parseInt(state.formLevelValue)) && parseInt(state.formLevelValue) > -1 && parseInt(state.formLevelValue) < 100;

    const saveCategory = () => {
        if (!isValid() || !props.postscriptStore) return;

        const {formNameValue, formLevelValue, formCheckValue} = state;
        const toSend: Partial<PostscriptCategory> = {
            name: formNameValue,
            level: parseInt(formLevelValue),
            check: (formCheckValue.value as PostscriptCategoryCheck),
        };

        (state.selectedCategory
                ? props.postscriptStore.updateCategory((state.selectedCategory as never as PostscriptCategory).id, toSend)
                : props.postscriptStore.createCategory(toSend)
        ).then(() => {
            props.postscriptStore!.fetchCategories().then(handleModalClose);
        });
    };

    const handleModalClose = () => {
        setState({
            showModal: false,
            selectedCategory: undefined,
            formNameValue: "",
            formLevelValue: "1",
            formCheckValue: checkOptions[1],
        });
    };

    const handleCreateClick = () => {
        setState(prevState => ({...prevState, showModal: true}));
    };

    const handleEditClick = (category: PostscriptCategory) => {
        setState({
            showModal: true,
            selectedCategory: category,
            formNameValue: category.name,
            formLevelValue: category.level.toString(),
            formCheckValue: {label: category.check, value: category.check},
        });
    };

    const handleDeleteClick = (category: PostscriptCategory) => {
        if (!props.appStore || !props.postscriptStore) return;
        props.postscriptStore!.deleteCategory(category.id);
    };

    const renderActionButton = (data: PostscriptCategory) => {
        return <ActionWrapper data={data} onEdit={handleEditClick} onDelete={handleDeleteClick}/>;
    };

    const renderCreateButton = () => (
        <div className="actions">
            <Button onClick={handleCreateClick}>create category</Button>
        </div>
    );

    const renderForm = () => {
        return (
            <React.Fragment>
                <Row>
                    <Col>
                        <Form.Group>
                            <Form.Label>Name</Form.Label>
                            <Form.Control onChange={handleNameChange} value={state.formNameValue}/>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group>
                            <Form.Label>Level</Form.Label>
                            <Form.Control type="number" onChange={handleLevelChange} value={state.formLevelValue}/>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <label>Check</label>
                        <SelectWrapper
                            options={checkOptions}
                            value={state.formCheckValue}
                            onChange={handleCheckChange}
                        />
                    </Col>
                </Row>
            </React.Fragment>
        );
    };

    if (!props.postscriptStore) return null;

    return (
        <>
            <div className={"list"}>
                <CategoriesListReactTable columns={columns} data={props.postscriptStore.categories.slice()}/>
            </div>
            {state.showModal
                ? <CrudModal
                    typeLabel={"Category"}
                    isEdit={!!state.selectedCategory}
                    renderForm={renderForm}
                    onClose={handleModalClose}
                    onSave={saveCategory}
                    isDisabled={!isValid() || props.postscriptStore.isUpdatingCategories}
                    showSpinner={props.postscriptStore.isUpdatingCategories}/> : null}
        </>
    );
}));

interface CategoriesListReactTableProps {
    columns: ColumnDef<PostscriptCategory>[]
    data: PostscriptCategory[]
}

function CategoriesListReactTable({columns, data}: CategoriesListReactTableProps) {
    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });
    return (
        <>
            <PageTitle pageTitle={"Manage Categories"}/>
            <Table>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th key={header.id}>
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {table.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                        ))}
                    </tr>
                ))}
                {table.getRowModel().rows.length === 0 && (<tr><td colSpan={4} className="text-center">No Categories</td></tr>)}
                </tbody>
            </Table>
            <div className={"pagination-bottom"}>
                <div className={"pagination"}>
                    <div className={"previous d-grid gap-2"}>
                        <Button disabled={!table.getCanPreviousPage()} onClick={() => table.previousPage()}>Previous</Button>
                    </div>
                    <div className={"center"}>
                        <span className={"page-info"}>
                            Page
                            <div className={"page-jump"}>
                                <Form.Control
                                    type="number"
                                    value={table.getState().pagination.pageIndex + 1}
                                    onChange={e => {
                                        const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                        const pageIndex = table.getState().pagination.pageIndex;
                                        if (page > pageIndex) {
                                            if (table.getCanNextPage()) {
                                                table.setPageIndex(page);
                                            }
                                        } else {
                                            if (table.getCanPreviousPage()) {
                                                table.setPageIndex(page);
                                            }
                                        }
                                    }}/>
                            </div>
                            of {" "}
                            <span className={"total-pages"}>{table.getPageCount()}</span>
                        </span>
                    </div>
                    <div className={"next d-grid gap-2"}>
                        <Button disabled={!table.getCanNextPage()} onClick={() => table.nextPage()}>Next</Button>
                    </div>
                </div>
            </div>
        </>
    );
}